import { useEffect, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Sidebar } from './Sidebar'
import { TopNav } from './TopNav'

export function MainLayout() {
  // Persist sidebar collapsed state in localStorage (key: 'sidebarCollapsed')
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
    try {
      // SSR safety: if window is undefined, default to open sidebar
      if (typeof window === 'undefined')
        return true
      const saved = localStorage.getItem('sidebarCollapsed')
      if (saved == null)
        return true
      const parsed = JSON.parse(saved)
      const isBool = typeof parsed === 'boolean'
      const collapsed = isBool ? parsed : false
      return !collapsed // sidebarOpen is inverse of collapsed
    }
    catch {
      return true
    }
  })
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)

  const sidebarWidth = sidebarOpen ? 'w-64' : 'w-20'
  const contentMargin = sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'

  // Save collapsed state whenever sidebarOpen changes
  useEffect(() => {
    try {
      localStorage.setItem('sidebarCollapsed', JSON.stringify(!sidebarOpen))
    }
    catch {}
  }, [sidebarOpen])

  return (
    <div className="relative min-h-screen">
      {/* Desktop Sidebar - Full Height */}
      <aside
        className={cn(
          'hidden lg:block fixed left-0 top-0 h-full transition-all duration-300 ease-in-out z-40',
          sidebarWidth,
        )}
      >
        <Sidebar isCollapsed={!sidebarOpen} />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-50 h-full w-64 transition-transform duration-300 ease-in-out lg:hidden',
          mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <Sidebar isCollapsed={false} onClose={() => setMobileSidebarOpen(false)} />
      </aside>

      {/* Top Navigation - Adjusts with sidebar */}
      <div className={cn('transition-all duration-300 ease-in-out', contentMargin)}>
        <TopNav
          onMenuClick={() => setSidebarOpen(!sidebarOpen)}
          onMobileMenuClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
        />
      </div>

      {/* Main Content - Adjusts with sidebar */}
      <main className={cn('min-h-screen transition-all duration-300 ease-in-out', contentMargin)}>
        <div className="container mx-auto px-4 py-6 lg:px-8">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
